import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, ShieldCheck, Broadcast, CurrencyDollar, Newspaper, ChartBar, Gear, CheckCircle, XCircle } from '@phosphor-icons/react';
import AutoSignalEngine from '@/components/app/AutoSignalEngine';
import AdminTrading from '@/components/app/AdminTrading';

export default function Admin() {
  const [overview, setOverview] = useState({});
  const [verifs, setVerifs] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedVerif, setSelectedVerif] = useState(null);
  const [notes, setNotes] = useState('');
  const [newsForm, setNewsForm] = useState({ title: '', category: 'FOREX', summary: '', content: '', image_url: '', source: '4xview Desk' });
  const [analysisForm, setAnalysisForm] = useState({ title: '', category: 'DAILY', asset: '', content: '', chart_image: '', video_url: '', pdf_url: '', tradingview_link: '' });
  const [templates, setTemplates] = useState({});
  const [emailLog, setEmailLog] = useState([]);
  const [emailTo, setEmailTo] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ subject: '', body: '' });

  const load = async () => {
    const [o, v, u, s, t, el] = await Promise.all([
      api.get('/admin/overview'), api.get('/admin/verifications'), api.get('/admin/users'),
      api.get('/admin/settings'), api.get('/admin/email/templates'), api.get('/admin/email/log'),
    ]);
    setOverview(o.data); setVerifs(v.data); setUsers(u.data); setSettings(s.data);
    setTemplates(t.data); setEmailLog(el.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, action) => {
    const fd = new FormData(); fd.append('notes', notes);
    await api.post(`/admin/verifications/${id}/${action}`, fd);
    toast.success(`Verification ${action}d`);
    setSelectedVerif(null); setNotes(''); load();
  };

  const grantPremium = async (uid) => {
    const fd = new FormData(); fd.append('days', '30');
    await api.post(`/admin/users/${uid}/grant-premium`, fd);
    toast.success('Premium granted for 30 days');
    load();
  };

  const publishNews = async (e) => {
    e.preventDefault();
    try { await api.post('/news', newsForm); toast.success('News published'); setNewsForm({...newsForm, title:'', summary:'', content:''}); } catch { toast.error('Failed'); }
  };
  const publishAnalysis = async (e) => {
    e.preventDefault();
    try { await api.post('/analysis', analysisForm); toast.success('Analysis published'); setAnalysisForm({...analysisForm, title:'', asset:'', content:''}); } catch { toast.error('Failed'); }
  };
  const saveSettings = async () => {
    await api.post('/admin/settings', settings); toast.success('Settings saved');
    load();
  };
  const testSmtp = async () => {
    try {
      // Save first, then send test to admin's own email
      await api.post('/admin/settings', settings);
      const admin = users.find(u => u.role === 'admin');
      const to = admin?.email || settings.smtp_from_email;
      if (!to) return toast.error('No admin email to test');
      await api.post('/admin/email/test', { to_email: to });
      toast.success(`Test email sent to ${to}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'SMTP test failed'); }
  };
  const saveTemplates = async () => {
    try { await api.post('/admin/email/templates', templates); toast.success('Templates saved'); }
    catch { toast.error('Failed'); }
  };
  const sendCustomEmail = async () => {
    try {
      await api.post('/admin/email/send', { to_email: emailTo.email, subject: emailDraft.subject, body: emailDraft.body });
      toast.success(`Email sent to ${emailTo.email}`);
      setEmailTo(null); setEmailDraft({ subject: '', body: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Email failed'); }
  };

  const cards = [
    { label: 'Total Users', value: overview.total_users, icon: Users },
    { label: 'Premium Users', value: overview.premium_users, icon: ShieldCheck },
    { label: 'Pending Verifications', value: overview.pending_verifications, icon: ShieldCheck },
    { label: 'Signals Today', value: overview.signals_today, icon: Broadcast },
    { label: 'Revenue', value: '$' + (overview.revenue ?? 0), icon: CurrencyDollar },
    { label: 'Active Subs', value: overview.active_subscriptions, icon: CurrencyDollar },
  ];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="data-label text-saffron">Admin Command Center</div>
        <h1 className="font-heading text-3xl font-bold mt-1">Overview</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map(({label, value, icon: I}) => (
          <div key={label} className="glass p-4 rounded-lg neon-border">
            <I size={20} className="text-saffron mb-2" weight="duotone" />
            <div className="data-label">{label}</div>
            <div className="stat-num text-2xl text-white mt-1">{value ?? 0}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="verifications" className="w-full">
        <TabsList className="glass border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="verifications" data-testid="tab-verifs">Verifications</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-analysis">Analysis</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Integrations</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email / SMTP</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chatwidget">Live Chat Widget</TabsTrigger>
          <TabsTrigger value="engine" data-testid="tab-engine">Auto Signal Engine</TabsTrigger>
          <TabsTrigger value="trading" data-testid="tab-trading">Trading Management</TabsTrigger>
        </TabsList>

        <TabsContent value="verifications" className="mt-4 space-y-3">
          {verifs.length === 0 && <div className="glass p-6 text-center text-muted-foreground rounded-lg">No verifications yet.</div>}
          {verifs.map(v => (
            <div key={v.id} className="glass p-4 rounded-lg flex items-center justify-between flex-wrap gap-3" data-testid={`verif-${v.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold">{v.name} <span className="text-xs text-muted-foreground ml-2">{v.user_email}</span></div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>📞 {v.phone || '—'}</span>
                  <span>🌍 {v.country || '—'}</span>
                  <span className="font-mono">{v.broker_name} · {v.account_number}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded ${v.status === 'pending' ? 'bg-saffron/15 text-saffron' : v.status === 'approved' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{v.status}</span>
                <Button size="sm" onClick={() => setSelectedVerif(v)} className="bg-white/5 hover:bg-white/10">View</Button>
              </div>
            </div>
          ))}

          {selectedVerif && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedVerif(null)}>
              <div className="glass-strong rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <h3 className="font-heading text-2xl font-bold mb-1">{selectedVerif.name}</h3>
                <div className="text-xs text-muted-foreground mb-4">{selectedVerif.user_email} · Submitted {new Date(selectedVerif.created_at).toLocaleString()}</div>
                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                  <div><span className="data-label">Full Name</span><div className="mt-1">{selectedVerif.name}</div></div>
                  <div><span className="data-label">Phone</span><div className="mt-1 font-mono" data-testid="verif-phone">{selectedVerif.phone || '—'}</div></div>
                  <div><span className="data-label">Country</span><div className="mt-1">{selectedVerif.country || '—'}</div></div>
                  <div><span className="data-label">Account Email</span><div className="mt-1">{selectedVerif.user_email}</div></div>
                  <div><span className="data-label">Broker</span><div className="mt-1">{selectedVerif.broker_name}</div></div>
                  <div><span className="data-label">Broker UID</span><div className="mt-1 font-mono">{selectedVerif.broker_uid}</div></div>
                  <div><span className="data-label">Account Number</span><div className="mt-1 font-mono">{selectedVerif.account_number}</div></div>
                  <div><span className="data-label">Broker Email</span><div className="mt-1">{selectedVerif.broker_email}</div></div>
                  {selectedVerif.notes && <div className="col-span-2"><span className="data-label">Client Notes</span><div className="mt-1 p-2 bg-surface-base rounded border border-white/5">{selectedVerif.notes}</div></div>}
                </div>
                {selectedVerif.screenshot_base64 && <img src={selectedVerif.screenshot_base64} alt="screenshot" className="w-full rounded-md border border-white/10 mb-4" />}
                {selectedVerif.status === 'pending' ? (
                  <>
                    <Textarea placeholder="Admin notes (will be emailed to client)..." value={notes} onChange={e => setNotes(e.target.value)} className="mb-3 bg-surface-base border-white/10" />
                    <div className="flex gap-2">
                      <Button onClick={() => decide(selectedVerif.id, 'approve')} data-testid="approve-btn" className="flex-1 bg-green-500 hover:bg-green-600"><CheckCircle size={16} className="mr-2" />Approve & Email</Button>
                      <Button onClick={() => decide(selectedVerif.id, 'reject')} data-testid="reject-btn" className="flex-1 bg-red-500 hover:bg-red-600"><XCircle size={16} className="mr-2" />Reject & Email</Button>
                    </div>
                  </>
                ) : (
                  <div className={`p-3 rounded-md ${selectedVerif.status === 'approved' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                    Status: <b>{selectedVerif.status}</b>. {selectedVerif.admin_notes && <span>Notes: {selectedVerif.admin_notes}</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-4 space-y-2">
          {users.map(u => (
            <div key={u.id} className="glass p-4 rounded-lg flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="font-semibold">{u.full_name} <span className="text-xs text-muted-foreground ml-2">{u.email}</span></div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                  <span>📞 {u.phone || '—'}</span>
                  <span>🌍 {u.country || '—'}</span>
                  <span>Role: {u.role}</span>
                  <span>{u.is_premium ? '⭐ Premium' : 'Free'}</span>
                  <span>Verif: {u.verification_status}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setEmailTo(u)} className="border-white/10 hover:bg-white/5">✉ Email</Button>
                {!u.is_premium && u.role !== 'admin' && <Button size="sm" onClick={() => grantPremium(u.id)} className="bg-saffron hover:bg-saffron-hover text-white">Grant 30d Premium</Button>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="news" className="mt-4">
          <form onSubmit={publishNews} className="glass p-6 rounded-lg space-y-3">
            <h3 className="font-heading font-bold text-lg">Publish News</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <Input required placeholder="Title" value={newsForm.title} onChange={e => setNewsForm({...newsForm, title: e.target.value})} className="bg-surface-base border-white/10" />
              <select value={newsForm.category} onChange={e => setNewsForm({...newsForm, category: e.target.value})} className="bg-surface-base border border-white/10 rounded-md h-10 px-3">
                {['FOREX','CRYPTO','GOLD','INDICES','ECONOMIC'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Textarea required placeholder="Summary" value={newsForm.summary} onChange={e => setNewsForm({...newsForm, summary: e.target.value})} className="bg-surface-base border-white/10" rows={2} />
            <Textarea required placeholder="Full content" value={newsForm.content} onChange={e => setNewsForm({...newsForm, content: e.target.value})} className="bg-surface-base border-white/10" rows={5} />
            <Button type="submit" className="bg-saffron hover:bg-saffron-hover text-white">Publish News</Button>
          </form>
        </TabsContent>

        <TabsContent value="analysis" className="mt-4">
          <form onSubmit={publishAnalysis} className="glass p-6 rounded-lg space-y-3">
            <h3 className="font-heading font-bold text-lg">Publish Market Analysis</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <Input required placeholder="Title" value={analysisForm.title} onChange={e => setAnalysisForm({...analysisForm, title: e.target.value})} className="bg-surface-base border-white/10" />
              <Input required placeholder="Asset (e.g. EURUSD)" value={analysisForm.asset} onChange={e => setAnalysisForm({...analysisForm, asset: e.target.value})} className="bg-surface-base border-white/10" />
              <select value={analysisForm.category} onChange={e => setAnalysisForm({...analysisForm, category: e.target.value})} className="bg-surface-base border border-white/10 rounded-md h-10 px-3">
                {['DAILY','WEEKLY','MONTHLY'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Textarea required placeholder="Analysis content" value={analysisForm.content} onChange={e => setAnalysisForm({...analysisForm, content: e.target.value})} className="bg-surface-base border-white/10" rows={6} />
            <Input placeholder="TradingView chart link" value={analysisForm.tradingview_link} onChange={e => setAnalysisForm({...analysisForm, tradingview_link: e.target.value})} className="bg-surface-base border-white/10" />
            <Button type="submit" className="bg-saffron hover:bg-saffron-hover text-white">Publish Analysis</Button>
          </form>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="glass p-6 rounded-lg space-y-4" data-testid="settings-panel">
            <h3 className="font-heading font-bold text-lg">Third-Party Integrations</h3>
            <p className="text-sm text-muted-foreground">Add your API keys here. Signals will auto-broadcast to configured channels.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="data-label">Telegram Bot Token</Label><Input type="password" value={settings.telegram_bot_token || ''} onChange={e => setSettings({...settings, telegram_bot_token: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="123456:ABC..." /></div>
              <div><Label className="data-label">Telegram Channel ID</Label><Input value={settings.telegram_channel_id || ''} onChange={e => setSettings({...settings, telegram_channel_id: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="@yourchannel or -100..." /></div>
              <div><Label className="data-label">WhatsApp API Key</Label><Input type="password" value={settings.whatsapp_api_key || ''} onChange={e => setSettings({...settings, whatsapp_api_key: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
              <div><Label className="data-label">WhatsApp Phone ID</Label><Input value={settings.whatsapp_phone_id || ''} onChange={e => setSettings({...settings, whatsapp_phone_id: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
              <div><Label className="data-label">Stripe Secret Key</Label><Input type="password" value={settings.stripe_secret_key || ''} onChange={e => setSettings({...settings, stripe_secret_key: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="sk_test_..." /></div>
              <div><Label className="data-label">Stripe Publishable Key</Label><Input value={settings.stripe_publishable_key || ''} onChange={e => setSettings({...settings, stripe_publishable_key: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="pk_test_..." /></div>
            </div>
            <Button onClick={saveSettings} data-testid="save-settings-btn" className="bg-saffron hover:bg-saffron-hover text-white">Save Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="email" className="mt-4 space-y-4" data-testid="email-panel">
          <div className="glass p-6 rounded-lg space-y-4">
            <h3 className="font-heading font-bold text-lg">SMTP Email Configuration</h3>
            <p className="text-sm text-muted-foreground">Add your email server credentials. Automated emails will be sent on user registration, verification submit, approval, rejection and subscription.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label className="data-label">SMTP Host</Label><Input value={settings.smtp_host || ''} onChange={e => setSettings({...settings, smtp_host: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="smtp.gmail.com / smtp.zoho.com / smtp.sendgrid.net" /></div>
              <div><Label className="data-label">SMTP Port</Label><Input type="number" value={settings.smtp_port || 587} onChange={e => setSettings({...settings, smtp_port: parseInt(e.target.value)||587})} className="mt-1 bg-surface-base border-white/10" placeholder="587 or 465" /></div>
              <div><Label className="data-label">SMTP Username</Label><Input value={settings.smtp_username || ''} onChange={e => setSettings({...settings, smtp_username: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
              <div><Label className="data-label">SMTP Password / App Password</Label><Input type="password" value={settings.smtp_password || ''} onChange={e => setSettings({...settings, smtp_password: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
              <div><Label className="data-label">From Email</Label><Input type="email" value={settings.smtp_from_email || ''} onChange={e => setSettings({...settings, smtp_from_email: e.target.value})} className="mt-1 bg-surface-base border-white/10" placeholder="noreply@4xview.com" /></div>
              <div><Label className="data-label">From Name</Label><Input value={settings.smtp_from_name || '4xview'} onChange={e => setSettings({...settings, smtp_from_name: e.target.value})} className="mt-1 bg-surface-base border-white/10" /></div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input id="tls" type="checkbox" checked={settings.smtp_use_tls !== false} onChange={e => setSettings({...settings, smtp_use_tls: e.target.checked})} />
                <Label htmlFor="tls" className="text-sm cursor-pointer">Use STARTTLS (port 587). Uncheck only if using port 465 (SSL) or unsecured.</Label>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={saveSettings} data-testid="save-smtp-btn" className="bg-saffron hover:bg-saffron-hover text-white">Save SMTP</Button>
              <Button onClick={testSmtp} variant="outline" data-testid="test-smtp-btn" className="border-white/10 hover:bg-white/5">Send test email to me</Button>
            </div>
          </div>

          <div className="glass p-6 rounded-lg space-y-3">
            <h3 className="font-heading font-bold text-lg">Email Templates</h3>
            <p className="text-sm text-muted-foreground">Available placeholders: <code className="text-saffron">{'{name}'}</code>, <code className="text-saffron">{'{email}'}</code>, <code className="text-saffron">{'{broker}'}</code>, <code className="text-saffron">{'{notes}'}</code>, <code className="text-saffron">{'{plan}'}</code>.</p>
            {Object.keys(templates).map(key => (
              <div key={key} className="border border-white/5 rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="data-label text-saffron">{key.replace('_',' ')}</span>
                </div>
                <Input value={templates[key]?.subject || ''} onChange={e => setTemplates({...templates, [key]: {...templates[key], subject: e.target.value}})} placeholder="Subject" className="bg-surface-base border-white/10" />
                <Textarea value={templates[key]?.body || ''} onChange={e => setTemplates({...templates, [key]: {...templates[key], body: e.target.value}})} rows={4} placeholder="HTML body" className="bg-surface-base border-white/10 font-mono text-xs" />
              </div>
            ))}
            <Button onClick={saveTemplates} data-testid="save-templates-btn" className="bg-saffron hover:bg-saffron-hover text-white">Save Templates</Button>
          </div>

          <div className="glass p-6 rounded-lg">
            <h3 className="font-heading font-bold text-lg mb-3">Recent Emails</h3>
            <div className="space-y-1 max-h-64 overflow-y-auto text-xs">
              {emailLog.length === 0 && <div className="text-muted-foreground">No emails sent yet.</div>}
              {emailLog.map(l => (
                <div key={l.id} className="flex justify-between p-2 rounded bg-surface-base">
                  <span className="font-mono">{l.to}</span>
                  <span className="truncate flex-1 mx-2 text-muted-foreground">{l.subject}</span>
                  <span className={l.status === 'sent' ? 'text-green-400' : 'text-red-400'}>{l.status}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-4">
          <div className="glass p-6 rounded-lg space-y-4" data-testid="chatwidget-panel">
            <h3 className="font-heading font-bold text-lg">Live Chat Widget</h3>
            <p className="text-sm text-muted-foreground">Enable a live-chat widget on the entire 4xview website. Paste the embed code from your provider — the widget will appear for all visitors.</p>
            <div className="flex items-center gap-3">
              <input id="chat-enabled" type="checkbox" checked={!!settings.chat_widget_enabled} onChange={e => setSettings({...settings, chat_widget_enabled: e.target.checked})} data-testid="chat-enabled" />
              <Label htmlFor="chat-enabled" className="cursor-pointer">Enable widget site-wide</Label>
            </div>
            <div>
              <Label className="data-label">Provider</Label>
              <select value={settings.chat_widget_provider || 'custom'} onChange={e => setSettings({...settings, chat_widget_provider: e.target.value})} className="mt-1 bg-surface-base border border-white/10 rounded-md h-10 px-3 w-full">
                <option value="tawkto">Tawk.to</option>
                <option value="crisp">Crisp</option>
                <option value="intercom">Intercom</option>
                <option value="tidio">Tidio</option>
                <option value="twilio">Twilio Flex / Frontline</option>
                <option value="chatwoot">Chatwoot</option>
                <option value="custom">Custom / Other</option>
              </select>
            </div>
            <div>
              <Label className="data-label">Embed Code (paste the full &lt;script&gt; snippet from your provider)</Label>
              <Textarea value={settings.chat_widget_code || ''} onChange={e => setSettings({...settings, chat_widget_code: e.target.value})} rows={10} className="mt-1 bg-surface-base border-white/10 font-mono text-xs" placeholder='<!--Start of Tawk.to Script-->\n<script type="text/javascript">\nvar Tawk_API=Tawk_API||{};\n...\n</script>' data-testid="chat-code" />
            </div>
            <Button onClick={saveSettings} data-testid="save-chatwidget-btn" className="bg-saffron hover:bg-saffron-hover text-white">Save Chat Widget</Button>
            <div className="text-xs text-muted-foreground pt-2 border-t border-white/5">
              <b>Quick links:</b>
              <ul className="mt-2 space-y-1 list-disc list-inside">
                <li><a href="https://www.tawk.to/" target="_blank" rel="noreferrer" className="text-saffron hover:underline">Tawk.to</a> — free forever, best value</li>
                <li><a href="https://crisp.chat/" target="_blank" rel="noreferrer" className="text-saffron hover:underline">Crisp.chat</a></li>
                <li><a href="https://www.intercom.com/" target="_blank" rel="noreferrer" className="text-saffron hover:underline">Intercom</a></li>
                <li><a href="https://www.twilio.com/flex" target="_blank" rel="noreferrer" className="text-saffron hover:underline">Twilio Flex</a></li>
              </ul>
              <div className="mt-2">Site-wide widget refresh: users need to reload the page after you enable/change this.</div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="engine" className="mt-4">
          <AutoSignalEngine />
        </TabsContent>

        <TabsContent value="trading" className="mt-4">
          <AdminTrading />
        </TabsContent>
      </Tabs>

      {emailTo && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setEmailTo(null)}>
          <div className="glass-strong rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-bold mb-1">Send email</h3>
            <div className="text-xs text-muted-foreground mb-3">To: {emailTo.email}</div>
            <Input value={emailDraft.subject} onChange={e => setEmailDraft({...emailDraft, subject: e.target.value})} placeholder="Subject" className="bg-surface-base border-white/10 mb-2" data-testid="email-subject" />
            <Textarea value={emailDraft.body} onChange={e => setEmailDraft({...emailDraft, body: e.target.value})} placeholder="HTML body allowed" rows={8} className="bg-surface-base border-white/10 mb-3" data-testid="email-body" />
            <div className="flex gap-2">
              <Button onClick={() => setEmailTo(null)} variant="outline" className="flex-1 border-white/10">Cancel</Button>
              <Button onClick={sendCustomEmail} data-testid="send-email-btn" className="flex-1 bg-saffron hover:bg-saffron-hover text-white">Send</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
